// One flat view of an assistant's widget theme.
//
// The API now returns a single flat object — GET /assistants/:id/config gives
// it as `theme`, the assistant record as `widgetTheme`, and PATCH takes it back
// as `widgetTheme`. It used to be grouped into brand / typography / launcher /
// layout, so this still falls back to those for anything stored before the
// change, and every screen reads the theme through here rather than reaching
// into a shape that may or may not be nested.

const pick = (value, fallback) =>
  value === undefined || value === null ? fallback : value;

/**
 * @param {object} source a config payload, an assistant record, or a theme
 * @returns {object} flat theme fields, with undefined where nothing was set
 */
export const readWidgetTheme = (source) => {
  const theme = source?.widgetTheme || source?.theme || source || {};
  const { brand = {}, typography = {}, launcher = {}, layout = {} } = theme;

  return {
    primaryColor: pick(theme.primaryColor, brand.primaryColor),
    headerColor: pick(theme.headerColor, brand.headerColor),
    accentColor: pick(theme.accentColor, brand.accentColor),
    buttonColor: pick(theme.buttonColor, brand.buttonColor),

    fontFamily: pick(theme.fontFamily, typography.fontFamily),
    fontSize: pick(theme.fontSize, typography.fontSize),
    messageSize: pick(theme.messageSize, typography.messageSize),
    assistantNameStyle: pick(
      theme.assistantNameStyle,
      typography.assistantNameStyle,
    ),

    launcherStyle: pick(theme.launcherStyle, launcher.style),
    avatarUrl: pick(theme.avatarUrl, launcher.avatarUrl),

    position: pick(theme.position, layout.position),
    positionOffsetX: pick(theme.positionOffsetX, layout.positionOffsetX),
    positionOffsetY: pick(theme.positionOffsetY, layout.positionOffsetY),
    borderRadius: pick(theme.borderRadius, layout.borderRadius),
    bubbleStyle: pick(theme.bubbleStyle, layout.bubbleStyle),
    // Renamed: `layout.theme` became `colorScheme`.
    colorScheme: pick(theme.colorScheme, layout.theme),
    spacing: pick(theme.spacing, layout.spacing),

    initialState: theme.initialState,
    autoOpen: theme.autoOpen,
    autoOpenAfterSeconds: theme.autoOpenAfterSeconds,
    showWelcomeMessage: theme.showWelcomeMessage,
    showAvatar: theme.showAvatar,
    allowUserToCloseWidget: theme.allowUserToCloseWidget,
    showPoweredBy: theme.showPoweredBy,
    showOnlineStatus: theme.showOnlineStatus,
    isAuthorizationRequired: theme.isAuthorizationRequired,

    // These live inside the theme now; older payloads had them alongside it.
    welcomeMessage: pick(theme.welcomeMessage, source?.welcomeMessage),
    suggestedPrompts: pick(theme.suggestedPrompts, source?.suggestedPrompts),
  };
};
